import type { ReviewContextPanelRef } from "@/services/api/agentic-v2/types";
import axios from "axios";
import { formatAniNotesRailDetail, readAniNotesParamsFromPanel } from "./aniNotesFromComments";

export function contextPanelRailDetail(panel: ReviewContextPanelRef): string | undefined {
  const params = panel.params ?? {};

  if (panel.kind === "briefing") {
    const subtitle = String(params.subtitle ?? "").trim();
    return subtitle || "Client context for this review";
  }

  if (panel.kind === "ani_notes") {
    return formatAniNotesRailDetail(readAniNotesParamsFromPanel(panel));
  }

  if (panel.kind === "follow_up_actions") {
    const actions = Array.isArray(params.actions) ? params.actions.length : 0;
    return actions > 0 ? `${actions} planned actions after approval` : "Planned follow-up actions";
  }

  return undefined;
}

export function hasFollowUpActionsPanel(panels: ReviewContextPanelRef[]): boolean {
  return panels.some((panel) => panel.kind === "follow_up_actions");
}

export const OPENWORK_LETTER_DEMO_TAG = "openwork_letter_demo";

export function isOpenworkLetterDemoTask(tags: string[] | null | undefined): boolean {
  return Boolean(tags?.includes(OPENWORK_LETTER_DEMO_TAG));
}

type OpenworkLetterReviewTaskLike = {
  tags?: string[] | null;
  scenario_ref?: string | null;
};

/** Home + Ani letter queue — excludes social/campaign demo tasks on the openworks tenant. */
export function isOpenworkLetterReviewTask(task: OpenworkLetterReviewTaskLike): boolean {
  const tags = task.tags ?? [];
  if (tags.includes("openwork_social_demo")) return false;
  return isOpenworkLetterDemoTask(tags) || Boolean(task.scenario_ref?.trim().startsWith("ow-"));
}

export function filterOpenworkLetterReviewTasks<T extends OpenworkLetterReviewTaskLike>(tasks: T[]): T[] {
  return tasks.filter(isOpenworkLetterReviewTask);
}

export type OpenworkLetterReviewTask = OpenworkLetterReviewTaskLike & {
  _id?: string;
  task_id?: string | null;
  status?: string | null;
  demo_unlock_after?: string | null;
  assigned_human_email?: string | null;
  updated_at?: string | null;
  demo_queue_rank?: number | null;
  name?: string | null;
};

/** Active letter-review tasks — excludes closed cases and locked addendums. */
export function isActiveOpenworkLetterReviewTask(task: OpenworkLetterReviewTask): boolean {
  const status = (task.status ?? "").trim();
  if (status === "done" || status === "auto_closed" || status === "cancelled") return false;
  if (status === "queued" && task.demo_unlock_after) return false;
  return true;
}

function openworkTaskRecencyKey(task: OpenworkLetterReviewTask): string {
  return task.updated_at ?? task._id ?? task.task_id ?? "";
}

/**
 * User-scoped, deduped OpenWork letter queue for Home + Ani.
 * One row per scenario_ref (latest reseed wins); typically 0–3 tasks.
 */
export function normalizeOpenworkLetterReviewQueue<T extends OpenworkLetterReviewTask>(
  tasks: T[],
  options?: { assigneeEmail?: string | null },
): T[] {
  const assigneeEmail = options?.assigneeEmail?.trim().toLowerCase();
  let filtered = filterOpenworkLetterReviewTasks(tasks).filter(isActiveOpenworkLetterReviewTask);
  if (assigneeEmail) {
    filtered = filtered.filter(
      (task) => (task.assigned_human_email ?? "").trim().toLowerCase() === assigneeEmail,
    );
  }

  const byScenario = new Map<string, T>();
  for (const task of filtered) {
    const scenarioRef = task.scenario_ref?.trim() || task._id || task.task_id || "";
    if (!scenarioRef) continue;
    const existing = byScenario.get(scenarioRef);
    if (!existing || openworkTaskRecencyKey(task).localeCompare(openworkTaskRecencyKey(existing)) > 0) {
      byScenario.set(scenarioRef, task);
    }
  }

  return [...byScenario.values()].sort((left, right) => {
    const rankDiff = (left.demo_queue_rank ?? 999) - (right.demo_queue_rank ?? 999);
    if (rankDiff !== 0) return rankDiff;
    return openworkTaskRecencyKey(right).localeCompare(openworkTaskRecencyKey(left));
  });
}

/** User-facing message when the letters list endpoint is missing (stale API process). */
export function formatOpenworkLettersLoadError(error: unknown): string {
  if (axios.isAxiosError(error) && error.response?.status === 404) {
    return "Could not load letters — the API process may be running without the latest OpenWork routes. Restart it with ./dev.sh start-api, then refresh.";
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return "Failed to load letters";
}
